import type { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import type {
    ChildTextContentElement,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import {
    reactiveWorkspaceActions,
    setReactiveWorkspaceActions,
} from 'api/graphQL/reactiveVars/workspaceActions'
import { workspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import { TrackChangeType } from 'models/changes'
import React from 'react'
import { getUpdatedItemsArray } from '../../WorkspaceContentArea'
import type { TextVariant } from '../../constants'
import {
    getParentContentId,
    getUpdatedElements,
} from '../ContentItemElementText'
import { TabsContext } from '../TabsContext'
import type { NormalizedDocumentTemplateViewItem } from '../model'
import { getUpdatedTemplateAfterPillAdd } from '../utils'

export type InsertPillProps = {
    fieldState?: Partial<TextContentElement>
    template?: NormalizedDocumentTemplateViewItem
    insertPillIndex?: number
    pillType: TextVariant
    variant?: TextVariant
    items?: Partial<ChildTextContentElement>[]
    textWithPill?: string
    setFieldState?: (input?: any) => void
    event?:
        | Pick<React.KeyboardEvent, 'currentTarget' | 'target'>
        | Pick<React.MouseEvent, 'currentTarget' | 'target'>
        | undefined
    titleText?: string
    value?: string
    element?: Partial<TextContentElement> | undefined
    changeType?: TrackChangeType
    setProperty?: (input?: any) => void
    saveChanges?: (
        updatedElementList: TextContentElement[],
        nonFormattedText: string,
        indexOfPillForNewPt?: number,
        typeOfPillForNewPt?: string,
        isPillWithNewPara?: boolean
    ) => void
}

export declare type InsertPillHandler<TProps = InsertPillProps> = {
    (props: TProps): void
}
/**
 * `useInsertPill` Custom hook that provides functionality for inserting a pill/text/UOM into a template.
 *                 Publishes the insertPill handler to make it available to entire website.
 * @returns {Object} An object containing:
 * - `insertPill`: A function that handles the insertion of a pill into a template.
 *                 This function takes an `InsertPillProps` object as a parameter, which includes `insertPillIndex`, `pillType`, `items`, and `textWithPill`.
 *                 The function updates the template and content items accordingly.
 * - `handleUpdateTemplate`: An optional function from `TabsContext` that updates the template.
 *
 * @example
 * const { insertPill, handleUpdateTemplate } = useInsertPill();
 */
export function useInsertPill() {
    const { handleUpdateTemplate } = React.useContext(TabsContext)
    let insertPillRef = React.useRef<InsertPillHandler>()

    // define insertPill handler
    insertPillRef.current = ({
        insertPillIndex,
        pillType,
        items,
        textWithPill,
    }: InsertPillProps) => {
        const {
            fieldState,
            template,
            changeType,
            textWithPill: textWithPillFromReactiveVars,
        } = reactiveWorkspaceActions().insertPillRequirements

        if (!textWithPill) {
            textWithPill = textWithPillFromReactiveVars
        }

        // don't provide insertPill handler if fieldState, template and updateTemplate handler are not defined
        if (!(fieldState && template && handleUpdateTemplate)) {
            insertPillRef.current = undefined
            return {
                insertPill: undefined,
                handleUpdateTemplate,
            }
        }
        const updatedElements = getUpdatedElements({
            fieldStateId: fieldState?.id,
            // if user enter text, then, creates a pill, use the textWithPill
            fieldStateText:
                !items && textWithPill ? textWithPill : fieldState?.text,
            positionIndex: insertPillIndex,
            type: pillType,
            fieldStateChangeType: changeType?.toUpperCase() as ChildTextContentElement['changeType'],
        })

        let updateValue: NormalizedDocumentTemplateViewItem = template
        let isRetrieveChanges: boolean = false
        let contentItemNodes: Partial<ContentItem>[] | undefined = undefined

        if (items && textWithPill) {
            // This block will append updated elements if user enter text first then creates a pill in a user-added and base content paragraph
            fieldState.elements?.push(
                ...(pillType === 'newReplacement' || pillType === 'newUOM'
                    ? items
                    : updatedElements)
            )

            // this block is only executed when textbox is inserted with new text
            if (pillType === 'newReplacement') {
                updateValue = getUpdatedTemplateAfterPillAdd({
                    id: undefined,
                    template,
                    type: pillType,
                    multipleSelectionType: pillType,
                    isNewReplacementWithtext: true, // this is set true to indicate that the textbox is added after new text
                    elementObjValue: {
                        ...fieldState,
                        text: ' ',
                        placeholderText: '',
                        elements: items,
                    },
                    fieldState: {
                        id: fieldState?.id,
                        text: fieldState?.text,
                        type: pillType,
                        parentOptionGroupId:
                            getParentContentId(template, fieldState) ?? '',
                    },
                })
                // this block is to check if the option group is already present in the updated template
                const elementHasOptionGroup: boolean =
                    updateValue?.contentItem?.elements?.filter(
                        (item) =>
                            item?.type === 'OptionGroup' &&
                            (item?.elements?.[0]?.type === 'SelectionOption' ||
                                item?.elements?.[0]?.type ===
                                    'ReplacementOption' ||
                                item?.elements?.[0]?.type === 'UnitsOfMeasure')
                    )?.length !== 0
                if (
                    elementHasOptionGroup &&
                    items?.[items?.length - 1]?.type !== 'newReplacement'
                ) {
                    updateValue.contentItem!.elements?.push(
                        items?.[items?.length - 1]
                    )
                    updateValue.contentItem!.elements = updateValue.contentItem!.elements?.filter(
                        (
                            value: Partial<TextContentElement>,
                            index: number,
                            self: Partial<TextContentElement>[]
                        ) =>
                            index ===
                            self.findIndex(
                                (itemIn) =>
                                    itemIn.id === value.id &&
                                    itemIn.text === value.text
                            )
                    )
                }
                contentItemNodes = getUpdatedItemsArray(
                    workspaceContents()?.contentItemNodes ?? [],
                    ([updateValue?.contentItem] ?? []) as Partial<
                        ContentItem
                    >[],
                    true,
                    false
                )
            }
        } else {
            // This block is for both pill only and text with pill in base content only or user-added only paragraph
            updateValue = getUpdatedTemplateAfterPillAdd({
                id: undefined,
                template,
                type: pillType,
                multipleSelectionType: pillType,
                elementObjValue: {
                    ...fieldState,
                    text: ' ',
                    placeholderText: '',
                    elements: updatedElements,
                },
                fieldState: {
                    id: fieldState?.id,
                    text: fieldState?.text,
                    type: pillType,
                    parentOptionGroupId:
                        getParentContentId(template, fieldState) ?? '',
                },
            })

            contentItemNodes = getUpdatedItemsArray(
                workspaceContents()?.contentItemNodes ?? [],
                ([updateValue?.contentItem] ?? []) as Partial<ContentItem>[],
                true,
                false
            )
        }

        handleUpdateTemplate(updateValue, isRetrieveChanges, contentItemNodes)
    }

    // side-effect to publish insertPill handler if not already published
    React.useEffect(() => {
        if (!reactiveWorkspaceActions().insertPill) {
            setReactiveWorkspaceActions({
                insertPill: insertPillRef.current,
            })

            // cleanup
            return () => {
                insertPillRef.current = undefined
            }
        }
    }, [insertPillRef.current, handleUpdateTemplate])

    // (optional) handleUpdateTemplate func from TabsContext
    return {
        insertPill: insertPillRef.current,
        handleUpdateTemplate,
    }
}
